import { useEffect } from 'react';
import { create } from 'zustand';

import { toast } from './useToast';

interface OnlineState {
  online: boolean;
  set: (v: boolean) => void;
}

const useOnlineStore = create<OnlineState>((set) => ({
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  set: (online) => set({ online }),
}));

/**
 * Hook to subscribe to online/offline events. Call once in App.
 * Surfaces a toast on reconnection so the user gets confirmation.
 */
export function useOnlineStatusListener(): void {
  const set = useOnlineStore((s) => s.set);
  useEffect(() => {
    let wasOffline = !navigator.onLine;
    const onOnline = () => {
      set(true);
      if (wasOffline) {
        toast.success('De retour en ligne 🎉');
        wasOffline = false;
      }
    };
    const onOffline = () => {
      set(false);
      wasOffline = true;
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [set]);
}

export const useOnlineStatus = () => useOnlineStore((s) => s.online);
